// Globals
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Input, Skeleton, Tooltip } from '@/ui';
import { FormItem, IFormInstance } from '@/components';
import { FormColumn, StyledWrapper, StyledButton, StyledSelect } from '../Styled';
import { CheckmarkIcon } from '@/icons';
import { RegionFieldLabel, RegionHintIcon, StyledForm, StyledTitle } from './CreateAdminFormStyles';

// Modules
import { useAutocompleteRegions } from '@/modules/Regions/hooks';

// Models
import { IAdminProfile } from '@/models/admins.model';

// Helpers
import { AdminFormFieldsNames, TAdminFormValues } from '../../helpers/types';
import { useAdminFormRules } from '../../hooks';
import { getRoleOptions } from '../../helpers/getOptions';

interface ICreateAdminForm {
  handleSubmit: (values: TAdminFormValues) => void;
  isLoading: boolean;
  isEditMode: boolean;
  form: IFormInstance;
  initialValues?: IAdminProfile | null;
}

const CreateAdminForm = ({ handleSubmit, isLoading, initialValues, form, isEditMode }: ICreateAdminForm) => {
  const { t } = useTranslation(['usersList', 'common']);
  const rules = useAdminFormRules();
  const { onSearch: onSearchRegions, regionsOptions } = useAutocompleteRegions(initialValues?.regionName ?? '', 0);

  const placeholder = t('common_placeholder_input', { ns: 'common' });
  const regionOptions = useMemo(() => {
    const options = regionsOptions ?? [];
    if (!initialValues?.regionId || options.some((option) => option.value === initialValues.regionId)) {
      return options;
    }

    return [{ label: initialValues.regionName, value: initialValues.regionId }, ...options];
  }, [initialValues?.regionId, initialValues?.regionName, regionsOptions]);

  if (isLoading) return <Skeleton />;

  return (
    <StyledWrapper>
      <StyledTitle>{t(isEditMode ? 'users_list_edit' : 'users_list_create_user')}</StyledTitle>

      <StyledForm onFinish={handleSubmit} initialValues={initialValues ?? {}} form={form}>
        <FormColumn>
          <FormItem
            name={AdminFormFieldsNames.FIRST_NAME}
            label={t('users_list_name')}
            rules={rules[AdminFormFieldsNames.FIRST_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>
          <FormItem
            name={AdminFormFieldsNames.LAST_NAME}
            label={t('users_list_last_name')}
            rules={rules[AdminFormFieldsNames.LAST_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>
          {isEditMode && (
            <FormItem name={AdminFormFieldsNames.ID} label={t('ID')}>
              <Input placeholder={placeholder} disabled />
            </FormItem>
          )}
          <FormItem
            name={AdminFormFieldsNames.ROLE}
            label={t('users_list_role')}
            rules={rules[AdminFormFieldsNames.ROLE]}
          >
            <StyledSelect options={getRoleOptions(t)} defaultValue={initialValues?.role} />
          </FormItem>
          <FormItem
            name={AdminFormFieldsNames.REGION_ID}
            label={
              <RegionFieldLabel>
                {t('users_list_region')}
                <Tooltip title={t('users_list_region_tooltip')}>
                  <RegionHintIcon />
                </Tooltip>
              </RegionFieldLabel>
            }
            extra={t('users_list_region_optional')}
          >
            <StyledSelect
              allowClear
              showSearch
              filterOption={false}
              options={regionOptions}
              onSearch={onSearchRegions}
              placeholder={t('users_list_region_placeholder')}
            />
          </FormItem>
          <FormItem
            name={AdminFormFieldsNames.EMAIL}
            label={t('users_list_email')}
            rules={rules[AdminFormFieldsNames.EMAIL]}
          >
            <Input placeholder={placeholder} />
          </FormItem>
        </FormColumn>

        <StyledButton htmlType="submit" loading={isLoading}>
          <CheckmarkIcon /> {t(isEditMode ? 'users_list_save' : 'users_list_create_user')}
        </StyledButton>
      </StyledForm>
    </StyledWrapper>
  );
};

export default CreateAdminForm;
